import React, { useState, useEffect, createContext, useContext, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Search,
  Link2,
  Target,
  CalendarDays,
  BarChart3,
  Trash2,
  ChevronLeft,
  Menu,
  X,
  Bot,
  FileText,
  HelpCircle,
  Sparkles,
  Settings,
  Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarStepItem, GlassCard, PillButton } from '@/components/ios26/IOSComponents';
import AssistantNudge from '@/components/ios26/AssistantNudge';
import DiagnosticsPanelFull from '@/components/ios26/DiagnosticsPanel';
import PlatformConfigModal from '@/components/ios26/PlatformConfigModal';
// P0 Social Setup Expert - Replaces legacy assistant in platform configuration context
import SocialSetupExpertDrawer from '@/components/ios26/SocialSetupExpertDrawer';
// P0 REFACTOR: Import XOAuth1Modal for X (Twitter) integration
import { XOAuth1Modal } from '@/components/XOAuth1Modal';
// iter 241 Lot 4 — Multimoteur V2 engine context (SEO + platforms
// isolation). Provider mounted around the guided layout so every
// step sees the same active engine.
import { EngineProvider } from '@/context/EngineContext';

// Import iOS 26 styles
import '@/styles/ios26-design-system.css';

/**
 * Project Step Context
 * ====================
 * Manages step progression and locking
 */
const ProjectStepContext = createContext(null);

export const useProjectSteps = () => {
  const context = useContext(ProjectStepContext);
  if (!context) {
    throw new Error('useProjectSteps must be used within ProjectStepProvider');
  }
  return context;
};

const STEPS = [
  { 
    id: 'analyze', 
    step: 1, 
    icon: '🔍', 
    title: 'Analyser mon site',
    lucideIcon: Search,
    description: 'L\'IA analyse votre contenu'
  },
  { 
    id: 'connect', 
    step: 2, 
    icon: '🔌', 
    title: 'Connecter mes comptes',
    lucideIcon: Link2,
    description: 'Reliez vos réseaux sociaux'
  },
  { 
    id: 'campaign', 
    step: 3, 
    icon: '🎯', 
    title: 'Créer une campagne',
    lucideIcon: Target,
    description: 'Définissez vos objectifs'
  },
  { 
    id: 'generate', 
    step: 4, 
    icon: '✨', 
    title: 'Générer 30 jours',
    lucideIcon: Sparkles,
    description: 'Un mois de contenu'
  },
  { 
    id: 'calendar', 
    step: 5, 
    icon: '🗓', 
    title: 'Calendrier éditorial',
    lucideIcon: CalendarDays,
    description: 'Gérez vos publications'
  },
  { 
    id: 'prospecting', 
    step: 6, 
    icon: '🧭', 
    title: 'Prospection CRM',
    lucideIcon: Users,
    description: 'Agent de prospection autonome',
    // P0 RESTORE iter249: global page, not scoped under /projects/:id/setup/
    globalRoute: '/prospecting'
  },
  { 
    id: 'results', 
    step: 7, 
    icon: '📊', 
    title: 'Résultats',
    lucideIcon: BarChart3,
    description: 'Suivez vos performances'
  },
  { 
    id: 'delete', 
    step: 8, 
    icon: '🗑', 
    title: 'Supprimer le projet',
    lucideIcon: Trash2,
    description: 'Supprimer définitivement',
    alwaysUnlocked: true,
    danger: true
  }
];

/**
 * Project Step Provider
 * P0 FIX: 
 * - currentStep is derived from ROUTE (not status)
 * - stepStatuses (completed/pending) from backend status
 * - Sidebar shows active step based on route, not just progress
 * 
 * P0 UX OVERHAUL (v2):
 * - getMissingPrerequisites() returns what's missing for each step
 * - canExecuteDestructiveAction() for blocking generation without prereqs
 */

// P0 FIX: Route pattern table (more robust than includes())
const ROUTE_TO_STEP = [
  { pattern: /\/setup\/analyze\/?$/, step: 'analyze' },
  { pattern: /\/setup\/connect\/?$/, step: 'connect' },
  { pattern: /\/setup\/campaign\/?$/, step: 'campaign' },
  { pattern: /\/setup\/generate\/?$/, step: 'generate' },
  { pattern: /\/setup\/calendar\/?$/, step: 'calendar' },
  { pattern: /\/setup\/results\/?$/, step: 'results' },
  { pattern: /\/analyze\/?$/, step: 'analyze' },
  { pattern: /\/connect\/?$/, step: 'connect' },
  { pattern: /\/campaign\/?$/, step: 'campaign' },
  { pattern: /\/generate\/?$/, step: 'generate' },
  { pattern: /\/calendar\/?$/, step: 'calendar' },
  { pattern: /\/results\/?$/, step: 'results' },
  { pattern: /\/prospecting\/?$/, step: 'prospecting' },
  { pattern: /\/delete\/?$/, step: 'delete' }
];

// P0 UX OVERHAUL v2: Prerequisites definition with human-readable labels
const STEP_PREREQUISITES = {
  analyze: [],
  connect: [],
  campaign: [],
  generate: [
    { stepId: 'connect', label: 'Connectez au moins 1 canal', check: (status) => status.channels_configured },
    { stepId: 'campaign', label: 'Configurez une campagne', check: (status) => status.campaign_configured }
  ],
  calendar: [
    { stepId: 'generate', label: 'Générez du contenu', check: (status) => status.content_generated }
  ],
  prospecting: [],
  results: [
    { stepId: 'generate', label: 'Générez du contenu', check: (status) => status.content_generated }
  ],
  delete: []
};

function matchRouteToStep(pathname) {
  for (const route of ROUTE_TO_STEP) {
    if (route.pattern.test(pathname)) {
      return route.step;
    }
  }
  return null; // No match
}

export function ProjectStepProvider({ children, projectId }) {
  const location = useLocation();
  const [stepStatuses, setStepStatuses] = useState({
    analyze: 'pending',
    connect: 'pending',
    campaign: 'pending',
    generate: 'pending',
    calendar: 'pending',
    prospecting: 'pending',
    results: 'pending',
    delete: 'pending'
  });
  const [loading, setLoading] = useState(true);
  const [projectData, setProjectData] = useState(null);
  
  // P0 FIX: Derive currentStep from route using pattern matching
  // Fallback to status-based detection if route doesn't match
  const currentStep = useMemo(() => {
    const matched = matchRouteToStep(location.pathname);
    
    if (matched) {
      return matched;
    }
    
    // Fallback: Use first incomplete step from status
    const firstIncomplete = STEPS.find(s => 
      stepStatuses[s.id] === 'pending' && 
      s.id !== 'delete' &&
      s.id !== 'prospecting' &&
      s.id !== 'results'
    );
    
    return firstIncomplete?.id || 'analyze';
  }, [location.pathname, stepStatuses]);
  
  // setCurrentStep is now a navigation function
  const navigate = useNavigate();
  const setCurrentStep = useCallback((stepId) => {
    const step = STEPS.find(s => s.id === stepId);
    if (step?.path) {
      navigate(step.path.replace(':projectId', projectId));
    }
  }, [navigate, projectId]);
  
  // Load project status from API - for stepStatuses (completed/pending), NOT currentStep
  useEffect(() => {
    const loadProjectStatus = async () => {
      try {
        const response = await apiClient.get(`/projects/${projectId}`);
        const project = response.data;
        setProjectData(project);
        
        // Use backend status object for completion states
        const status = project.status || {};
        
        // Step 1: Analyze - completed if website_analyzed AND themes validated
        const analyzeStatus = status.website_analyzed 
          ? (status.themes_validated ? 'completed' : 'warning')
          : 'pending';
        
        // Step 2: Connect - completed if at least one channel connected
        const connectStatus = status.channels_configured ? 'completed' : 'pending';
        
        // Step 3: Campaign - completed if campaign created
        const campaignStatus = status.campaign_configured ? 'completed' : 'pending';
        
        // Step 4: Generate - completed if content generated
        const generateStatus = status.content_generated ? 'completed' : 'pending';
        
        // Step 5: Calendar - available after content generated
        const calendarStatus = status.content_generated ? 'pending' : 'locked';
        
        // Step 6: Results - available after content generated
        const resultsStatus = status.content_generated ? 'pending' : 'locked';
        
        const statuses = {
          analyze: analyzeStatus,
          connect: connectStatus,
          campaign: campaignStatus,
          generate: generateStatus,
          calendar: calendarStatus,
          prospecting: 'pending',
          results: resultsStatus,
          delete: 'pending'
        };
        
        setStepStatuses(statuses);
      } catch (error) {
        console.error('Failed to load project status:', error);
      } finally {
        setLoading(false);
      }
    };
    
    if (projectId) {
      loadProjectStatus();
    }
  }, [projectId]);
  
  // P0 FIX: isStepLocked now checks prerequisites, not sequential order
  const isStepLocked = useCallback((stepId) => {
    const step = STEPS.find(s => s.id === stepId);
    if (step?.alwaysUnlocked) return false;
    
    // Define prerequisites for each step
    const prerequisites = {
      analyze: [], // Always accessible
      connect: [], // Always accessible (user can connect first)
      campaign: [], // Accessible but may show warning if no channels
      generate: ['campaign'], // Needs campaign configured
      calendar: ['generate'], // Needs content generated
      prospecting: [], // P0 RESTORE iter249: always accessible, independent CRM module
      results: ['generate'], // Needs content generated
      delete: [] // Always accessible
    };
    
    const stepPrereqs = prerequisites[stepId] || [];
    
    // Check if all prerequisites are completed
    for (const prereq of stepPrereqs) {
      if (stepStatuses[prereq] !== 'completed') {
        return true;
      }
    }
    
    return false;
  }, [stepStatuses]);
  
  // P0 UX OVERHAUL v2: Get missing prerequisites for a step
  const getMissingPrerequisites = useCallback((stepId) => {
    const prereqs = STEP_PREREQUISITES[stepId] || [];
    const status = projectData?.status || {};
    
    return prereqs.filter(prereq => !prereq.check(status));
  }, [projectData]);
  
  // P0 UX OVERHAUL v2: Check if destructive action can be executed
  const canExecuteDestructiveAction = useCallback((stepId) => {
    const missing = getMissingPrerequisites(stepId);
    return missing.length === 0;
  }, [getMissingPrerequisites]);
  
  const completeStep = async (stepId) => {
    setStepStatuses(prev => ({
      ...prev,
      [stepId]: 'completed'
    }));
    
    // Navigate to next step
    const stepIndex = STEPS.findIndex(s => s.id === stepId);
    const nextStep = STEPS[stepIndex + 1];
    if (nextStep && !nextStep.alwaysUnlocked) {
      setCurrentStep(nextStep.id);
    }
  };
  
  const setStepStatus = (stepId, status) => {
    setStepStatuses(prev => ({
      ...prev,
      [stepId]: status
    }));
  };
  
  // Refresh project data
  const refreshProject = async () => {
    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      const project = response.data;
      setProjectData(project);
      
      // Re-compute statuses from fresh backend data
      const status = project.status || {};
      
      const statuses = {
        analyze: status.website_analyzed 
          ? (status.themes_validated ? 'completed' : 'warning')
          : 'pending',
        connect: status.channels_configured ? 'completed' : 'pending',
        campaign: status.campaign_configured ? 'completed' : 'pending',
        generate: status.content_generated ? 'completed' : 'pending',
        prospecting: 'pending',
        results: status.content_generated ? 'pending' : 'locked',
        delete: 'pending'
      };
      
      setStepStatuses(statuses);
    } catch (error) {
      console.error('Failed to refresh project:', error);
    }
  };
  
  return (
    <ProjectStepContext.Provider value={{
      steps: STEPS,
      currentStep,  // Now derived from route
      setCurrentStep, // Now navigates
      stepStatuses,
      setStepStatus,
      completeStep,
      isStepLocked,
      loading,
      projectData,
      refreshProject,
      // P0 UX OVERHAUL v2: Prerequisites helpers
      getMissingPrerequisites,
      canExecuteDestructiveAction,
      STEP_PREREQUISITES
    }}>
      {children}
    </ProjectStepContext.Provider>
  );
}

/**
 * Guided Project Sidebar
 * USES SEMANTIC TOKENS for dark mode compatibility
 * P0 UX OVERHAUL v2: Shows prerequisite badges
 */
export function GuidedSidebar({ projectId, projectName }) {
  const navigate = useNavigate();
  const { steps, currentStep, stepStatuses, getMissingPrerequisites } = useProjectSteps();
  const [mobileOpen, setMobileOpen] = useState(false);
  
  // P0 UX OVERHAUL: Navigation libre - la sidebar ne bloque plus
  const handleStepClick = (stepId) => {
    // Navigation directe vers l'étape via route
    const step = steps.find(s => s.id === stepId);
    if (step) {
      // P0 RESTORE iter249: Prospection CRM is a global page (not scoped
      // under /projects/:id/setup/), same route AppShellLayout already
      // links to. Never duplicate the route/component.
      navigate(step.globalRoute || `/projects/${projectId}/setup/${stepId}`);
    }
    setMobileOpen(false);
  };
  
  return (
    <>
      {/* Mobile Toggle */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-xl shadow-lg"
        style={{ backgroundColor: 'hsl(var(--color-surface))' }}
      >
        <Menu className="w-6 h-6 text-semantic" />
      </button>
      
      {/* Mobile Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/30 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside 
        className={cn(
          'fixed top-0 left-0 z-40 h-full w-72 backdrop-blur-xl',
          'flex flex-col transition-transform duration-300',
          'lg:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
        style={{ 
          backgroundColor: 'hsl(var(--sidebar-bg))',
          borderRight: '1px solid hsl(var(--color-border))'
        }}
      >
        {/* Header */}
        <div 
          className="p-5"
          style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}
        >
          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-sm transition-colors"
              style={{ color: 'hsl(var(--sidebar-text))' }}
            >
              <ChevronLeft className="w-4 h-4" />
              Retour
            </button>
            <button
              onClick={() => setMobileOpen(false)}
              className="lg:hidden p-1 rounded transition-colors"
              style={{ backgroundColor: 'transparent' }}
            >
              <X className="w-5 h-5 text-semantic-muted" />
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-semantic truncate">{projectName || 'Mon projet'}</h2>
              <p className="text-xs text-semantic-muted">Configuration guidée</p>
            </div>
          </div>
        </div>
        
        {/* Steps */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <div 
            className="text-xs font-semibold uppercase tracking-wider px-3 mb-2"
            style={{ color: 'hsl(var(--sidebar-text-disabled))' }}
          >
            Étapes
          </div>
          
          <div className="space-y-1">
            {steps.map((step) => {
              const status = stepStatuses[step.id];
              const isActive = currentStep === step.id;
              
              // P0 UX OVERHAUL v2: Get missing prerequisites for badge
              const missingPrereqs = getMissingPrerequisites(step.id);
              const hasMissingPrereqs = missingPrereqs.length > 0;
              
              // P0 UX OVERHAUL: Statut visuel sans blocage
              let stepStatus = 'pending';
              if (status === 'completed') stepStatus = 'completed';
              else if (status === 'error') stepStatus = 'error';
              else if (status === 'warning') stepStatus = 'warning';
              else if (isActive) stepStatus = 'active';
              
              return (
                <SidebarStepItem
                  key={step.id}
                  step={step.step}
                  title={step.title}
                  icon={step.icon}
                  status={stepStatus}
                  locked={false}  // P0: Navigation toujours libre
                  hasMissingPrereqs={hasMissingPrereqs}  // P0 v2: Badge prérequis
                  onClick={() => handleStepClick(step.id)}
                  className={step.danger && 'text-red-500 hover:bg-red-50'}
                  data-testid={`sidebar-step-${step.id}`}
                />
              );
            })}
          </div>
        </nav>
        
        {/* Footer */}
        <div 
          className="p-4"
          style={{ borderTop: '1px solid hsl(var(--color-border-subtle))' }}
        >
          <button
            onClick={() => navigate(`/projects/${projectId}/settings`)}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors text-sm"
            style={{ 
              color: 'hsl(var(--sidebar-text))',
              backgroundColor: 'transparent'
            }}
          >
            <Settings className="w-4 h-4" />
            Paramètres du projet
          </button>
        </div>
      </aside>
    </>
  );
}

/**
 * Diagnostics Panel (Right Side)
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
export function DiagnosticsPanel({ onClose }) {
  const [activeTab, setActiveTab] = useState('assistant');
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: '👋 Bonjour ! Je suis votre assistant technique. Je peux vous aider à configurer vos comptes et résoudre les erreurs. Que puis-je faire pour vous ?'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  
  const tabs = [
    { id: 'assistant', label: 'Assistant', icon: Bot },
    { id: 'diagnostic', label: 'Diagnostic', icon: FileText },
    { id: 'support', label: 'Support', icon: HelpCircle }
  ];
  
  const handleSendMessage = async () => {
    if (!inputValue.trim() || sending) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { type: 'user', content: userMessage }]);
    setSending(true);
    
    try {
      // Call AI assistant API
      const response = await apiClient.post('/ai/tech-assistant', {
        message: userMessage,
        context: 'setup'
      });
      
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: response.data.response 
      }]);
    } catch (error) {
      setMessages(prev => [...prev, { 
        type: 'ai', 
        content: 'Désolé, je n\'ai pas pu traiter votre demande. Réessayez dans quelques instants.'
      }]);
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div 
      className="fixed top-0 right-0 z-40 h-full w-96 backdrop-blur-xl flex flex-col shadow-xl"
      style={{ 
        backgroundColor: 'hsl(var(--color-surface) / 0.95)',
        borderLeft: '1px solid hsl(var(--color-border))'
      }}
    >
      {/* Header */}
      <div 
        className="p-4 flex items-center justify-between"
        style={{ borderBottom: '1px solid hsl(var(--color-border-subtle))' }}
      >
        <h3 className="font-semibold text-semantic">Aide & Diagnostic</h3>
        <button 
          onClick={onClose}
          className="p-1.5 rounded-lg transition-colors"
          style={{ backgroundColor: 'transparent' }}
        >
          <X className="w-5 h-5 text-semantic-muted" />
        </button>
      </div>
      
      {/* Tabs */}
      <div 
        className="flex p-1.5 gap-1"
        style={{ 
          borderBottom: '1px solid hsl(var(--color-border-subtle))',
          backgroundColor: 'hsl(var(--color-surface-2))'
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-sm font-medium transition-all"
            style={{
              backgroundColor: activeTab === tab.id 
                ? 'hsl(var(--color-primary))' 
                : 'transparent',
              color: activeTab === tab.id 
                ? 'hsl(var(--color-primary-text))' 
                : 'hsl(var(--color-text-muted))'
            }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'assistant' && (
          <div 
            className="h-full flex flex-col"
            style={{ backgroundColor: 'hsl(var(--chat-bg))' }}
          >
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className="max-w-[85%] p-3 rounded-2xl text-sm"
                  style={{
                    marginLeft: msg.type === 'user' ? 'auto' : '0',
                    borderRadius: msg.type === 'user' 
                      ? '1rem 1rem 0.25rem 1rem' 
                      : '1rem 1rem 1rem 0.25rem',
                    backgroundColor: msg.type === 'user' 
                      ? 'hsl(var(--chat-bubble-user))' 
                      : 'hsl(var(--chat-bubble-agent))',
                    color: msg.type === 'user' 
                      ? 'hsl(var(--chat-bubble-user-text))' 
                      : 'hsl(var(--chat-bubble-agent-text))'
                  }}
                >
                  <div 
                    className="whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{ 
                      __html: msg.content.replace(/\n/g, '<br/>') 
                    }}
                  />
                </div>
              ))}
              {sending && (
                <div 
                  className="rounded-2xl rounded-bl-md p-3 max-w-[85%]"
                  style={{ 
                    backgroundColor: 'hsl(var(--chat-bubble-agent))',
                    color: 'hsl(var(--chat-bubble-agent-text))'
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'hsl(var(--color-text-muted))' }} />
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'hsl(var(--color-text-muted))', animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'hsl(var(--color-text-muted))', animationDelay: '0.4s' }} />
                  </div>
                </div>
              )}
            </div>
            
            {/* Input - CRITICAL: Must use semantic tokens for dark mode */}
            <div 
              className="p-4"
              style={{ 
                borderTop: '1px solid hsl(var(--color-border))',
                backgroundColor: 'hsl(var(--color-surface))'
              }}
            >
              <div className="flex gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Posez votre question..."
                  className="flex-1 px-4 py-2.5 rounded-full text-sm focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'hsl(var(--chat-input-bg))',
                    color: 'hsl(var(--chat-input-text))',
                    border: '1px solid hsl(var(--chat-input-border))',
                    caretColor: 'hsl(var(--color-primary))'
                  }}
                  data-testid="guided-assistant-input"
                />
                <PillButton 
                  variant="primary" 
                  size="sm"
                  onClick={handleSendMessage}
                  loading={sending}
                >
                  Envoyer
                </PillButton>
              </div>
              <p 
                className="text-xs mt-2 text-center"
                style={{ color: 'hsl(var(--chat-input-placeholder))' }}
              >
                Collez une erreur ou posez une question
              </p>
            </div>
          </div>
        )}
        
        {activeTab === 'diagnostic' && (
          <DiagnosticTab />
        )}
        
        {activeTab === 'support' && (
          <SupportTab />
        )}
      </div>
    </div>
  );
}

/**
 * Diagnostic Tab Content
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
function DiagnosticTab() {
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const loadDiagnostics = async () => {
      try {
        const response = await apiClient.get('/support/diagnostics/recent');
        setDiagnostics(response.data);
      } catch (error) {
        console.error('Failed to load diagnostics:', error);
      } finally {
        setLoading(false);
      }
    };
    loadDiagnostics();
  }, []);
  
  if (loading) {
    return (
      <div className="p-6 text-center">
        <div 
          className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto" 
          style={{ borderColor: 'hsl(var(--color-primary))', borderTopColor: 'transparent' }}
        />
        <p className="text-sm text-semantic-muted mt-2">Chargement...</p>
      </div>
    );
  }
  
  return (
    <div className="p-4 space-y-4 overflow-y-auto h-full">
      <div className="text-sm text-semantic-muted mb-4">
        Dernières connexions et erreurs
      </div>
      
      {diagnostics?.recent_errors?.length > 0 ? (
        diagnostics.recent_errors.slice(0, 5).map((error, i) => (
          <div 
            key={i} 
            className="p-3 rounded-xl"
            style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
          >
            <div className="flex items-center justify-between mb-2">
              <span className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full',
                error.transient_hint ? 'chip-semantic-warning' : 'chip-semantic-danger'
              )}>
                {error.transient_hint ? '⏳ transient' : '❌ erreur'}
              </span>
              <span className="text-xs text-semantic-muted">
                {new Date(error.created_at).toLocaleTimeString('fr')}
              </span>
            </div>
            <p className="text-sm font-medium text-semantic">{error.error_code}</p>
            <p className="text-xs text-semantic-muted truncate">{error.message}</p>
          </div>
        ))
      ) : (
        <div className="text-center py-8">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: 'hsl(var(--color-success-bg))' }}
          >
            <span style={{ color: 'hsl(var(--color-success))' }}>✅</span>
          </div>
          <p className="text-sm text-semantic-2">Aucune erreur récente</p>
          <p className="text-xs text-semantic-muted">Tout fonctionne correctement</p>
        </div>
      )}
      
      <PillButton variant="secondary" className="w-full">
        Copier le diagnostic
      </PillButton>
    </div>
  );
}

/**
 * Support Tab Content
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
function SupportTab() {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !message.trim()) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setSending(true);
    try {
      await apiClient.post('/support/ticket', { subject, message });
      toast.success('Ticket envoyé ! Nous répondons sous 24h.');
      setSubject('');
      setMessage('');
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="p-4 overflow-y-auto h-full">
      <div className="text-sm text-semantic-muted mb-4">
        Besoin d&apos;aide ? Ouvrez un ticket.
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-semantic mb-1">
            Sujet
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Ex: Problème de connexion Instagram"
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 input-semantic"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-semantic mb-1">
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Décrivez votre problème..."
            rows={4}
            className="w-full px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:ring-2 resize-none input-semantic"
          />
        </div>
        
        <div 
          className="flex items-center gap-2 p-3 rounded-xl"
          style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
        >
          <input 
            type="checkbox" 
            id="attach-logs-guided" 
            className="rounded"
            style={{ accentColor: 'hsl(var(--color-primary))' }}
          />
          <label 
            htmlFor="attach-logs-guided" 
            className="text-sm"
            style={{ color: 'hsl(var(--color-info-text))' }}
          >
            Joindre les logs techniques (recommandé)
          </label>
        </div>
        
        <PillButton 
          type="submit" 
          variant="primary" 
          className="w-full"
          loading={sending}
        >
          Envoyer le ticket
        </PillButton>
      </form>
    </div>
  );
}

/**
 * Main Guided Project Layout
 * USES SEMANTIC TOKENS for dark mode compatibility
 */
export default function GuidedProjectLayout({ children }) {
  const { projectId } = useParams();
  const [projectName, setProjectName] = useState('');
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  
  // P0 SOCIAL SETUP EXPERT: Track which platform the user needs help with
  // When set, shows SocialSetupExpertDrawer instead of legacy DiagnosticsPanel
  const [expertPlatform, setExpertPlatform] = useState(null);
  
  useEffect(() => {
    const loadProject = async () => {
      try {
        const response = await apiClient.get(`/projects/${projectId}`);
        setProjectName(response.data.name);
      } catch (error) {
        console.error('Failed to load project:', error);
      }
    };
    if (projectId) loadProject();
  }, [projectId]);
  
  // Listen for assistant open event from nudge
  useEffect(() => {
    const handleOpenAssistant = () => {
      setShowDiagnostics(true);
    };
    
    window.addEventListener('ame:open-assistant', handleOpenAssistant);
    return () => window.removeEventListener('ame:open-assistant', handleOpenAssistant);
  }, []);
  
  // P0 SOCIAL SETUP EXPERT: Listen for platform-specific help requests
  // This event opens the expert drawer directly for a specific platform
  useEffect(() => {
    const handleOpenSocialExpert = (event) => {
      const { platform } = event.detail || {};
      if (platform) {
        setExpertPlatform(platform);
        setShowDiagnostics(true);
      }
    };
    
    window.addEventListener('ame:open-social-expert', handleOpenSocialExpert);
    return () => window.removeEventListener('ame:open-social-expert', handleOpenSocialExpert);
  }, []);
  
  // P0 UX OVERHAUL v2: Platform modal with proper event payload and cleanup
  const [platformModalOpen, setPlatformModalOpen] = useState(null);
  const [lastEventId, setLastEventId] = useState(null); // Prevent duplicate events
  
  useEffect(() => {
    const handleOpenPlatformModal = (event) => {
      const { platform, projectId: eventProjectId, source, eventId } = event.detail || {};
      
      // P0 UX v2: Prevent duplicate event handling
      if (eventId && eventId === lastEventId) {
        return;
      }
      
      // P0 UX v2: Validate payload
      if (!platform) {
        console.warn('[ame:open-platform-modal] Missing platform in payload');
        return;
      }
      
      // P0 UX v2: Optionally check if event is for this project
      if (eventProjectId && eventProjectId !== projectId) {
        return; // Event not for this project
      }
      
      if (eventId) setLastEventId(eventId);
      setPlatformModalOpen(platform);
      
      // P0 SOCIAL SETUP EXPERT: Also set the expert platform context
      // so help button shows relevant guidance
      setExpertPlatform(platform);
    };
    
    window.addEventListener('ame:open-platform-modal', handleOpenPlatformModal);
    return () => window.removeEventListener('ame:open-platform-modal', handleOpenPlatformModal);
  }, [projectId, lastEventId]);
  
  // Broadcast assistant state changes
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ame:assistant-state', { 
      detail: { open: showDiagnostics } 
    }));
  }, [showDiagnostics]);
  
  // P0 SOCIAL SETUP EXPERT: Derive if we're on a platform configuration page
  const location = useLocation();
  const isOnConnectPage = location.pathname.includes('/connect');
  
  // P0: Handle Assistant IA button click
  // On Connect page, always open Social Setup Expert (with platform selector if no platform selected)
  const handleAssistantToggle = () => {
    if (showDiagnostics) {
      // Close the panel
      setShowDiagnostics(false);
      setExpertPlatform(null);
    } else {
      // Open the panel
      setShowDiagnostics(true);
      // If on Connect page, force the expert mode (even without a specific platform)
      // The drawer will show the platform selector
      if (isOnConnectPage) {
        // Set a special flag to indicate we want the expert drawer but without a specific platform
        // The drawer handles this case by showing a platform selector
        setExpertPlatform('__selector__'); // Special value to show selector
      }
    }
  };
  
  return (
    <EngineProvider projectId={projectId}>
    <ProjectStepProvider projectId={projectId}>
      <div className="min-h-screen ios-bg-mesh">
        {/* Sidebar */}
        <GuidedSidebar projectId={projectId} projectName={projectName} />
        
        {/* Main Content */}
        <main className={cn(
          'transition-all duration-300',
          'lg:ml-72',
          showDiagnostics && 'lg:mr-96'
        )}>
          {/* Top Bar */}
          <header 
            className="sticky top-0 z-30 backdrop-blur-xl"
            style={{ 
              backgroundColor: 'hsl(var(--color-surface) / 0.7)',
              borderBottom: '1px solid hsl(var(--color-border))'
            }}
          >
            <div className="flex items-center justify-between px-6 py-4">
              <div className="lg:hidden" /> {/* Spacer for mobile menu button */}
              <div className="flex-1" />
              <button
                onClick={handleAssistantToggle}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all"
                style={{
                  backgroundColor: showDiagnostics 
                    ? 'hsl(var(--color-primary))' 
                    : 'hsl(var(--color-surface-2))',
                  color: showDiagnostics 
                    ? 'hsl(var(--color-primary-text))' 
                    : 'hsl(var(--color-text-2))'
                }}
                data-testid="assistant-toggle-btn"
              >
                <Bot className="w-4 h-4" />
                Assistant IA
              </button>
            </div>
          </header>
          
          {/* Page Content */}
          <div className="p-6 lg:p-10">
            {children}
          </div>
        </main>
        
        {/* P0 SOCIAL SETUP EXPERT: Context-aware help panel
            - On Connect page: Always show SocialSetupExpertDrawer (with selector if no platform)
            - If expertPlatform is set: Show SocialSetupExpertDrawer for that platform
            - On other pages without platform context: Show legacy DiagnosticsPanelFull
            
            The legacy assistant should no longer appear when configuring platforms.
        */}
        {showDiagnostics && (
          (expertPlatform || isOnConnectPage) ? (
            <SocialSetupExpertDrawer
              isOpen={true}
              onClose={() => {
                setShowDiagnostics(false);
                setExpertPlatform(null);
              }}
              platform={expertPlatform}
              projectId={projectId}
              onAction={(action) => {
                // Handle actions from the expert drawer
                if (action?.type === 'open_modal') {
                  setPlatformModalOpen(action.platform);
                } else if (action?.type === 'switch_platform') {
                  setExpertPlatform(action.platform);
                }
              }}
            />
          ) : (
            <DiagnosticsPanelFull 
              onClose={() => setShowDiagnostics(false)} 
              projectId={projectId} 
            />
          )
        )}
        
        {/* Assistant Nudge Popup - Only show when expert drawer is NOT open */}
        <AssistantNudge assistantOpen={showDiagnostics} />
        
        {/* P0 REFACTOR: X OAuth 1.0a Modal (for x and x_twitter platforms) */}
        <XOAuth1Modal
          isOpen={platformModalOpen === 'x' || platformModalOpen === 'x_twitter'}
          onClose={() => setPlatformModalOpen(null)}
          projectId={projectId}
          onStatusChange={(status) => {
            // Refresh project data after status change
            if (status?.connected) {
              window.dispatchEvent(new CustomEvent('ame:platform-connected', {
                detail: { platform: 'x' }
              }));
            }
          }}
        />
        
        {/* P0 UX OVERHAUL: Platform Configuration Modal (for non-X platforms) */}
        {platformModalOpen && platformModalOpen !== 'x' && platformModalOpen !== 'x_twitter' && (
          <PlatformConfigModal
            platformId={platformModalOpen}
            isOpen={true}
            onClose={() => setPlatformModalOpen(null)}
            onConnect={(platformId) => {
              // Refresh project data after successful connection
              window.dispatchEvent(new CustomEvent('ame:platform-connected', {
                detail: { platform: platformId }
              }));
            }}
          />
        )}
      </div>
    </ProjectStepProvider>
    </EngineProvider>
  );
}

export { STEPS };
